import { motion } from "framer-motion";
import { Calendar, PawPrint } from "lucide-react";
import { DOCTORS, PIKKO } from "../../lib/pikko";

const ease = [0.2, 0.8, 0.2, 1];

export default function Team() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative py-24 md:py-32 bg-[var(--pikko-cream)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 lg:mb-20 items-end">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">— Jadwal Layanan</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight text-[var(--pikko-brown-ink)]">
              Professional{" "}
              <span className="italic text-[var(--pikko-orange)]">
                Veterinary
              </span>{" "}
              Team
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease }}
            className="lg:col-span-5 text-[15px] leading-relaxed text-[var(--pikko-brown-deep)]/85"
          >
            Tim medis Pikko Pet Care siap memberikan pelayanan terbaik bagi
            anjing, kucing, hingga hewan exotic. Disarankan booking sebelum
            datang agar lebih nyaman tanpa antrian.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-9">
          {DOCTORS.map((m, i) => (
            <motion.article
              key={m.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, delay: i * 0.12, ease }}
              data-testid={`team-card-${i}`}
              className="group soft-card overflow-hidden flex flex-col"
            >
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={m.img}
                  alt={m.name}
                  className="w-full h-full object-cover warm-grade transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                  style={{ objectPosition: m.pos }}
                  loading="lazy"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 60%, rgba(46,34,24,0.22) 100%)",
                  }}
                />
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[rgba(251,247,238,0.92)] backdrop-blur-md text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-orange-dark)] font-semibold">
                  {m.role}
                </div>
              </div>
              <div className="p-6 lg:p-7 flex-1 flex flex-col">
                <h3 className="font-serif text-[26px] leading-tight text-[var(--pikko-brown-ink)]">
                  {m.name}
                </h3>
                <p className="mt-2 text-[13px] text-[var(--pikko-brown-deep)]/80">
                  {m.desc}
                </p>

                {/* Animals served */}
                <div className="mt-5 pt-5 border-t border-[rgba(138,107,82,0.14)] flex flex-wrap gap-1.5">
                  {m.animals.map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--pikko-cream)] border border-[rgba(138,107,82,0.18)] text-[11px] text-[var(--pikko-brown-deep)] font-medium"
                    >
                      <PawPrint className="w-3 h-3 text-[var(--pikko-orange-dark)]" />
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Booking note */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between p-6 lg:p-7 rounded-3xl bg-[var(--pikko-cream-warm)] border border-[rgba(138,107,82,0.14)]"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-[var(--pikko-orange)]/12 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-[var(--pikko-orange-dark)]" />
            </div>
            <div>
              <div className="font-serif text-[18px] text-[var(--pikko-brown-ink)] leading-tight">
                Disarankan booking sebelum datang
              </div>
              <div className="text-[13px] text-[var(--pikko-brown-deep)]/75 mt-0.5">
                Agar lebih nyaman tanpa antrian.
              </div>
            </div>
          </div>
          <a
            href={PIKKO.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="team-booking-cta"
            className="btn-primary text-sm whitespace-nowrap"
          >
            Konsultasi &amp; Booking
          </a>
        </motion.div>
      </div>
    </section>
  );
}
